import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { inventoryService, jobService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";
import {
  formatDistanceToNow,
  format,
  parseISO,
  isToday,
  startOfMonth,
  getDay,
} from "date-fns";

// Helper function to format currency
function formatGBP(amount: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(amount);
}

// Component for dashboard cards
function DashboardCard({
  title,
  value,
  icon,
  description,
  trend,
  loading = false,
  error = false,
}: {
  title: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  description?: string;
  trend?: {
    value: number;
    isPositive: boolean;
    label: string;
  };
  loading?: boolean;
  error?: boolean;
}) {
  return (
    <div className="bg-white overflow-hidden shadow-sm rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0 bg-primary/10 rounded-md p-3">
            {icon}
          </div>
          <div className="ml-5 w-0 flex-1">
            <dt className="text-sm font-medium text-gray-500 truncate">
              {title}
            </dt>
            <dd className="flex items-baseline">
              {loading ? (
                <div className="h-8 w-24 bg-gray-200 animate-pulse rounded"></div>
              ) : error ? (
                <div className="text-sm text-red-500">Failed to load data</div>
              ) : (
                <div className="text-2xl font-semibold text-dark">{value}</div>
              )}
            </dd>
            {description && (
              <p className="mt-1 text-sm text-gray-500">{description}</p>
            )}
          </div>
        </div>
        {trend && !loading && !error && (
          <div className="mt-4">
            <div className="flex items-center">
              {trend.isPositive ? (
                <svg
                  className="w-4 h-4 text-green-500 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
              ) : (
                <svg
                  className="w-4 h-4 text-red-500 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6"
                  />
                </svg>
              )}
              <span
                className={`text-sm ${
                  trend.isPositive ? "text-green-600" : "text-red-600"
                }`}
              >
                {trend.value}% {trend.label}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Component for recent activity items
function ActivityItem({
  title,
  description,
  time,
  icon,
}: {
  title: string;
  description: string;
  time: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="flex items-start space-x-3 py-3">
      <div className="flex-shrink-0 bg-gray-100 rounded-full p-2">{icon}</div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900">{title}</p>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
      <div className="flex-shrink-0 whitespace-nowrap text-sm text-gray-500">
        {time}
      </div>
    </div>
  );
}

// Mini Calendar Component
function MiniCalendar({
  events,
  onDateClick,
}: {
  events: any[];
  onDateClick: (date: Date) => void;
}) {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today);

  // Calculate days for the calendar
  const days = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const startDay = getDay(monthStart);
    const daysArray = [];

    // Add empty days for the start of the month
    for (let i = 0; i < startDay; i++) {
      daysArray.push(null);
    }

    // Add days of the month
    let day = 1;
    while (day <= 31) {
      const date = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        day
      );
      if (date.getMonth() === currentMonth.getMonth()) {
        daysArray.push(date);
      }
      day++;
    }

    return daysArray;
  }, [currentMonth]);

  // Check if a date has events
  const hasEvents = (date: Date) => {
    return events.some((event) => {
      // Use job date if available, fall back to prep_date
      const eventDateStr = event.date || event.prep_date;

      // Skip if no valid date is found
      if (!eventDateStr) return false;

      try {
        const eventDate = parseISO(eventDateStr);
        return (
          eventDate.getDate() === date.getDate() &&
          eventDate.getMonth() === date.getMonth() &&
          eventDate.getFullYear() === date.getFullYear()
        );
      } catch (error) {
        // If parsing fails, skip this event
        return false;
      }
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={() =>
            setCurrentMonth(
              new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
            )
          }
          className="p-1 rounded hover:bg-gray-100"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-gray-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
        <h3 className="font-medium text-gray-900">
          {format(currentMonth, "MMMM yyyy")}
        </h3>
        <button
          onClick={() =>
            setCurrentMonth(
              new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
            )
          }
          className="p-1 rounded hover:bg-gray-100"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5 text-gray-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-500 mb-1">
        {["S", "M", "T", "W", "T", "F", "S"].map((day, i) => (
          <div key={i} className="py-1">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {days.map((day, i) => {
          const hasEventsOnDay = day ? hasEvents(day) : false;
          return (
            <div
              key={i}
              className={`
                py-1 rounded-full w-8 h-8 mx-auto flex items-center justify-center text-sm
                ${day ? "cursor-pointer hover:bg-gray-100" : ""}
                ${
                  day && isToday(day) ? "bg-primary text-white font-medium" : ""
                }
                ${
                  day && hasEventsOnDay && !isToday(day)
                    ? "bg-primary/20 font-medium"
                    : ""
                }
              `}
              onClick={() => day && onDateClick(day)}
            >
              {day ? day.getDate() : ""}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { currentOrganization } = useAuth();
  const navigate = useNavigate();
  const [timeframe, setTimeframe] = useState<"week" | "month" | "year">(
    "month"
  );

  // Fetch inventory data
  const {
    data: inventoryData,
    isLoading: inventoryLoading,
    error: inventoryError,
  } = useQuery({
    queryKey: ["inventory", currentOrganization?.id],
    queryFn: () => inventoryService.getInventoryItems(currentOrganization?.id),
    enabled: !!currentOrganization?.id,
  });

  // Fetch jobs data
  const {
    data: jobsData = [],
    isLoading: jobsLoading,
    error: jobsError,
  } = useQuery({
    queryKey: ["jobs", currentOrganization?.id],
    queryFn: () => jobService.getJobs(currentOrganization?.id),
    enabled: !!currentOrganization?.id,
  });

  // Calculate inventory metrics
  const totalItems = inventoryData?.length || 0;
  const totalAssets =
    inventoryData?.reduce((sum, item) => sum + (item.assets?.length || 0), 0) ||
    0;

  // Calculate total inventory value
  const totalValue =
    inventoryData?.reduce((sum, item) => {
      return sum + (item.purchase_price || 0) * (item.assets?.length || 0);
    }, 0) || 0;

  // Calculate utilization rate
  const checkedOutAssets =
    inventoryData?.reduce((sum, item) => {
      return (
        sum +
        (item.assets?.filter((asset: any) => asset.status === "checked_out")
          .length || 0)
      );
    }, 0) || 0;

  const utilizationRate = totalAssets
    ? Math.round((checkedOutAssets / totalAssets) * 100)
    : 0;

  // Get unique categories
  const categories = new Set<string>();
  inventoryData?.forEach((item) => {
    if (item.category) {
      categories.add(item.category);
    }
  });

  // Get recently added items
  const recentItems = [...(inventoryData || [])]
    .sort((a, b) => {
      return (
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    })
    .slice(0, 5);

  // Get upcoming jobs
  const upcomingJobs = useMemo(() => {
    return [...(jobsData || [])]
      .filter(
        (job) => job.status === "upcoming" || job.status === "in_progress"
      )
      .sort((a, b) => {
        return (
          new Date(a.prep_date).getTime() - new Date(b.prep_date).getTime()
        );
      })
      .slice(0, 5);
  }, [jobsData]);

  // Handle calendar date click
  const handleDateClick = (_date: Date) => {
    navigate(`/org/${currentOrganization?.id}/calendar`);
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex justify-between items-center pb-5 border-b border-gray-200">
        <div>
          <h3 className="text-lg font-medium leading-6 text-dark">
            Dashboard Overview
          </h3>
          <p className="mt-2 max-w-4xl text-sm text-gray-500">
            Welcome to {currentOrganization?.name || "your"} dashboard. Here's
            an overview of your operations.
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setTimeframe("week")}
            className={`px-3 py-1 text-sm rounded-md ${
              timeframe === "week"
                ? "bg-primary text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Week
          </button>
          <button
            onClick={() => setTimeframe("month")}
            className={`px-3 py-1 text-sm rounded-md ${
              timeframe === "month"
                ? "bg-primary text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Month
          </button>
          <button
            onClick={() => setTimeframe("year")}
            className={`px-3 py-1 text-sm rounded-md ${
              timeframe === "year"
                ? "bg-primary text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Year
          </button>
        </div>
      </div>

      {/* Dashboard cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {/* Inventory Items Card */}
        <DashboardCard
          title="Inventory Items"
          value={totalItems}
          loading={inventoryLoading}
          error={!!inventoryError}
          icon={
            <svg
              className="h-6 w-6 text-primary"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
              />
            </svg>
          }
          description={`Across ${categories.size} categories`}
        />

        {/* Total Assets Card */}
        <DashboardCard
          title="Total Assets"
          value={totalAssets}
          loading={inventoryLoading}
          error={!!inventoryError}
          icon={
            <svg
              className="h-6 w-6 text-primary"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
              />
            </svg>
          }
          description={`${checkedOutAssets} currently checked out`}
        />

        {/* Utilization Rate Card */}
        <DashboardCard
          title="Utilization Rate"
          value={`${utilizationRate}%`}
          loading={inventoryLoading}
          error={!!inventoryError}
          icon={
            <svg
              className="h-6 w-6 text-primary"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
          }
          trend={{
            value: 5,
            isPositive: true,
            label: "vs last month",
          }}
        />

        {/* Total Value Card */}
        <DashboardCard
          title="Total Value"
          value={formatGBP(totalValue)}
          loading={inventoryLoading}
          error={!!inventoryError}
          icon={
            <svg
              className="h-6 w-6 text-primary"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          }
          description="Based on purchase prices"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Recent Activity */}
        <div className="bg-white overflow-hidden shadow-sm rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Recent Activity
            </h3>
            <div className="space-y-3 divide-y divide-gray-200">
              {inventoryLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="py-3 flex items-center space-x-3">
                    <div className="h-10 w-10 bg-gray-200 animate-pulse rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 animate-pulse rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-gray-200 animate-pulse rounded w-1/2"></div>
                    </div>
                  </div>
                ))
              ) : inventoryError ? (
                <div className="py-3 text-center text-red-500">
                  Failed to load recent activity
                </div>
              ) : recentItems.length > 0 ? (
                recentItems.map((item) => (
                  <ActivityItem
                    key={item.id}
                    title={item.name}
                    description={`New ${item.category} item added to inventory`}
                    time={formatDistanceToNow(new Date(item.created_at), {
                      addSuffix: true,
                    })}
                    icon={
                      <svg
                        className="h-5 w-5 text-gray-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                        />
                      </svg>
                    }
                  />
                ))
              ) : (
                <div className="py-3 text-center text-gray-500">
                  No recent activity to show
                </div>
              )}
            </div>
            <div className="mt-4 text-center">
              <Link
                to={`/org/${currentOrganization?.id}/inventory`}
                className="text-primary text-sm hover:underline"
              >
                View all inventory
              </Link>
            </div>
          </div>
        </div>

        {/* Upcoming Jobs */}
        <div className="bg-white overflow-hidden shadow-sm rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Upcoming Jobs
            </h3>
            <div className="space-y-3 divide-y divide-gray-200">
              {jobsLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="py-3">
                    <div className="h-5 bg-gray-200 animate-pulse rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 animate-pulse rounded w-1/2 mb-1"></div>
                    <div className="h-4 bg-gray-200 animate-pulse rounded w-1/4"></div>
                  </div>
                ))
              ) : jobsError ? (
                <div className="py-3 text-center text-red-500">
                  Failed to load upcoming jobs
                </div>
              ) : upcomingJobs.length > 0 ? (
                upcomingJobs.map((job) => (
                  <Link
                    key={job.id}
                    to={`/org/${currentOrganization?.id}/jobs/${job.id}`}
                    className="block py-3 hover:bg-gray-50 -mx-4 px-4 transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {job.title}
                        </h4>
                        <p className="text-sm text-gray-500">
                          {job.project_number}
                        </p>
                        <div className="mt-1 flex items-center text-xs text-gray-500">
                          <svg
                            className="h-4 w-4 mr-1 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          <span>
                            Prep:{" "}
                            {format(parseISO(job.prep_date), "dd MMM yyyy")}
                          </span>
                        </div>
                      </div>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          job.status === "upcoming"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {job.status === "in_progress"
                          ? "In Progress"
                          : "Upcoming"}
                      </span>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="py-3 text-center text-gray-500">
                  No upcoming jobs to show
                </div>
              )}
            </div>
            <div className="mt-4 text-center">
              <Link
                to={`/org/${currentOrganization?.id}/jobs`}
                className="text-primary text-sm hover:underline"
              >
                View all jobs
              </Link>
            </div>
          </div>
        </div>

        {/* Right Column - Calendar and Team */}
        <div className="space-y-6">
          {/* Mini Calendar */}
          <div className="bg-white overflow-hidden shadow-sm rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Calendar</h3>
                <Link
                  to={`/org/${currentOrganization?.id}/calendar`}
                  className="text-primary text-sm hover:underline"
                >
                  View full calendar
                </Link>
              </div>
              <MiniCalendar
                events={jobsData || []}
                onDateClick={handleDateClick}
              />
            </div>
          </div>
          {/* Team Members section removed */}
        </div>
      </div>
    </div>
  );
}
